import apiClient from './client';

// Transcription status enum matching backend
export type TranscriptionStatus = 'pending' | 'queued' | 'processing' | 'completed' | 'failed' | 'cancelled';

// Transcription segment
export interface TranscriptionSegment {
  id: number;
  start: number;
  end: number;
  text: string;
  translation?: string;
  is_modified?: boolean;
  speaker?: string;
  confidence?: number;
}

export interface TranscriptionSpeaker {
  id: string;
  name: string;
  segments_count?: number;
}

export interface SegmentEdit {
  id: number;
  text: string;
}

// Transcription response
export interface TranscriptionResponse {
  id: string;
  file_id: string;
  folder_id?: string | null;
  file_status?: string;
  file_name?: string;
  file_original_name?: string;
  source_url?: string;
  display_name?: string;
  display_name_user_set?: boolean;
  user_id: string;
  status: TranscriptionStatus;
  language: string;
  duration: number;
  speaker_count?: number;
  content_preview?: string;
  word_count?: number;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  progress?: number;
  processing_time_seconds?: number;
  is_edited?: boolean;
  last_edited_at?: string;
}

// List transcriptions response
export interface ListTranscriptionsResponse {
  transcriptions: TranscriptionResponse[];
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

// Create transcription request
export interface CreateTranscriptionRequest {
  file_id: string;
  language?: string;
  options?: {
    speaker_diarization?: boolean;
    word_timestamps?: boolean;
    punctuation?: boolean;
  };
}

export async function createTranscription(data: CreateTranscriptionRequest): Promise<TranscriptionResponse> {
  const response = await apiClient.post<TranscriptionResponse>('/transcriptions', data);
  return response.data;
}

// List transcriptions with pagination
export interface ListTranscriptionsParams {
  page?: number;
  page_size?: number;
  status?: TranscriptionStatus;
  file_id?: string;
  folder_id?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
}

export async function listTranscriptions(params?: ListTranscriptionsParams): Promise<ListTranscriptionsResponse> {
  const response = await apiClient.get<ListTranscriptionsResponse>('/transcriptions', { params });
  return response.data;
}

// Get transcription details
export async function getTranscription(transcriptionId: string): Promise<TranscriptionResponse> {
  const response = await apiClient.get<TranscriptionResponse>(`/transcriptions/${transcriptionId}`);
  return response.data;
}

export async function moveTranscription(transcriptionId: string, folderId: string | null): Promise<TranscriptionResponse> {
  const response = await apiClient.post<TranscriptionResponse>(`/transcriptions/${transcriptionId}/move`, {
    folder_id: folderId,
  });
  return response.data;
}

export async function moveTranscriptions(transcriptionIds: string[], folderId: string | null): Promise<void> {
  await apiClient.post('/transcriptions/move', {
    transcription_ids: transcriptionIds,
    folder_id: folderId,
  });
}

export interface TranscriptionFolder {
  id: string;
  name: string;
  created_at: string;
  updated_at: string;
}

export interface ListTranscriptionFoldersResponse {
  folders: TranscriptionFolder[];
}

export async function listTranscriptionFolders(): Promise<ListTranscriptionFoldersResponse> {
  const response = await apiClient.get<ListTranscriptionFoldersResponse>('/transcription-folders');
  return response.data;
}

export async function createTranscriptionFolder(name: string): Promise<TranscriptionFolder> {
  const response = await apiClient.post<TranscriptionFolder>('/transcription-folders', { name });
  return response.data;
}

export async function renameTranscriptionFolder(folderId: string, name: string): Promise<TranscriptionFolder> {
  const response = await apiClient.patch<TranscriptionFolder>(`/transcription-folders/${folderId}`, { name });
  return response.data;
}

export async function deleteTranscriptionFolder(folderId: string): Promise<void> {
  await apiClient.delete(`/transcription-folders/${folderId}`);
}

export interface TranscriptionFolderStats {
  total: number;
  uncategorized: number;
  by_folder_id: Record<string, number>;
}

export async function getTranscriptionFolderStats(params?: { status?: TranscriptionStatus }): Promise<TranscriptionFolderStats> {
  const response = await apiClient.get<TranscriptionFolderStats>('/transcription-folders/stats', { params });
  return response.data;
}

// Get transcription status (lightweight polling endpoint)
export interface TranscriptionStatusResponse {
  status: TranscriptionStatus;
  progress?: number;
  error_message?: string;
  started_at?: string;
  completed_at?: string;
  processing_time_seconds?: number;
}

export async function getTranscriptionStatus(transcriptionId: string): Promise<TranscriptionStatusResponse> {
  const response = await apiClient.get<TranscriptionStatusResponse>(`/transcriptions/${transcriptionId}/status`);
  return response.data;
}

// Get transcription result (segments)
export interface TranscriptionResultResponse {
  transcription_id: string;
  segments: TranscriptionSegment[];
  speakers?: TranscriptionSpeaker[];
  content?: string;
  metadata?: {
    language: string;
    duration: number;
    word_count: number;
    speaker_count: number;
  };
  target_language?: string;
}

export interface TranslateTranscriptionRequest {
  target_language: string;
}

export interface TranslateTranscriptionResponse {
  status: TranscriptionStatus;
  target_language?: string;
}

export async function getTranscriptionResult(transcriptionId: string): Promise<TranscriptionResultResponse> {
  const response = await apiClient.get<TranscriptionResultResponse>(`/transcriptions/${transcriptionId}/result?lite=1`);
  return response.data;
}

export async function translateTranscription(
  transcriptionId: string,
  targetLanguage: string
): Promise<TranslateTranscriptionResponse> {
  const response = await apiClient.post<TranslateTranscriptionResponse>(
    `/transcriptions/${transcriptionId}/translate`,
    { target_language: targetLanguage } satisfies TranslateTranscriptionRequest
  );
  return response.data;
}

export type MindmapStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface MindmapResponse {
  status: MindmapStatus;
  source_version: string;
  outline_markdown?: string;
  error?: string;
  message?: string;
}

export type AIArtifactStatus = 'not_started' | 'pending' | 'processing' | 'completed' | 'failed';

export interface TranscriptionAIStatusResponse {
  summary: {
    status: AIArtifactStatus;
    source_version?: string;
    template_id: string;
    error?: string;
  };
  keypoints: {
    status: AIArtifactStatus;
    source_version?: string;
    template_id?: string;
    output_language?: string;
    error?: string;
  };
  mindmap: {
    status: AIArtifactStatus;
    source_version?: string;
    error?: string;
  };
  translations: {
    available_languages: string[];
  };
}

export async function getTranscriptionAIStatus(
  transcriptionId: string,
  templateId: string
): Promise<TranscriptionAIStatusResponse> {
  const response = await apiClient.get<TranscriptionAIStatusResponse>(`/transcriptions/${transcriptionId}/ai-status`, {
    params: { template_id: templateId }
  });
  return response.data;
}

export async function requestMindmap(transcriptionId: string): Promise<MindmapResponse> {
  const response = await apiClient.post<MindmapResponse>(`/transcriptions/${transcriptionId}/mindmap`);
  return response.data;
}

export async function getMindmap(transcriptionId: string): Promise<MindmapResponse> {
  const response = await apiClient.get<MindmapResponse>(`/transcriptions/${transcriptionId}/mindmap`);
  return response.data;
}

export interface SummaryTemplate {
  id: string;
  user_id?: string;
  is_system: boolean;
  name: string;
  prompt: string;
  archived_at?: string;
  created_at: string;
  updated_at: string;
}

export interface ListSummaryTemplatesResponse {
  templates: SummaryTemplate[];
}

export async function listSummaryTemplates(): Promise<ListSummaryTemplatesResponse> {
  const response = await apiClient.get<ListSummaryTemplatesResponse>('/summary/templates');
  return response.data;
}

export interface CreateSummaryTemplateRequest {
  name: string;
  prompt: string;
}

export async function createSummaryTemplate(data: CreateSummaryTemplateRequest): Promise<SummaryTemplate> {
  const response = await apiClient.post<SummaryTemplate>('/summary/templates', data);
  return response.data;
}

export interface UpdateSummaryTemplateRequest {
  name: string;
  prompt: string;
}

export async function updateSummaryTemplate(templateId: string, data: UpdateSummaryTemplateRequest): Promise<SummaryTemplate> {
  const response = await apiClient.patch<SummaryTemplate>(`/summary/templates/${templateId}`, data);
  return response.data;
}

export async function deleteSummaryTemplate(templateId: string): Promise<void> {
  await apiClient.delete(`/summary/templates/${templateId}`);
}

export type SummaryStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface SummaryResponse {
  status: SummaryStatus;
  source_version: string;
  template_id: string;
  summary_markdown?: string;
  error?: string;
  message?: string;
}

export interface RequestSummaryRequest {
  template_id: string;
}

export async function requestSummary(transcriptionId: string, templateId: string): Promise<SummaryResponse> {
  const response = await apiClient.post<SummaryResponse>(`/transcriptions/${transcriptionId}/summary`, {
    template_id: templateId
  } satisfies RequestSummaryRequest);
  return response.data;
}

export async function getSummary(transcriptionId: string, templateId: string): Promise<SummaryResponse> {
  const response = await apiClient.get<SummaryResponse>(`/transcriptions/${transcriptionId}/summary`, {
    params: { template_id: templateId }
  });
  return response.data;
}

export type KeypointsStatus = 'pending' | 'processing' | 'completed' | 'failed';

export interface KeypointsItem {
  question: string;
  answer: string;
}

export interface KeypointsResponse {
  status: KeypointsStatus;
  source_version: string;
  template_id: string;
  output_language: string;
  items?: KeypointsItem[];
  error?: string;
  message?: string;
}

export async function requestKeypoints(transcriptionId: string): Promise<KeypointsResponse> {
  const response = await apiClient.post<KeypointsResponse>(`/transcriptions/${transcriptionId}/keypoints`);
  return response.data;
}

export async function getKeypoints(transcriptionId: string): Promise<KeypointsResponse> {
  const response = await apiClient.get<KeypointsResponse>(`/transcriptions/${transcriptionId}/keypoints`);
  return response.data;
}

export interface UpdateContentRequest {
  segments: SegmentEdit[];
}

export interface UpdateContentResponse {
  id: string;
  is_edited: boolean;
  last_edited_at: string;
  word_count: number;
}

export async function updateTranscriptionContent(
  transcriptionId: string,
  segments: SegmentEdit[]
): Promise<UpdateContentResponse> {
  const response = await apiClient.put<UpdateContentResponse>(`/transcriptions/${transcriptionId}/content`, {
    segments
  } satisfies UpdateContentRequest);
  return response.data;
}

export interface UpdateSpeakerRequest {
  segment_id: number;
  new_speaker: string;
  scope: 'segment' | 'all_same';
}

export interface UpdateSpeakerResponse {
  id: string;
  is_edited: boolean;
  last_edited_at: string;
  speaker_count: number;
}

export async function updateTranscriptionSpeaker(
  transcriptionId: string,
  req: UpdateSpeakerRequest
): Promise<UpdateSpeakerResponse> {
  const response = await apiClient.put<UpdateSpeakerResponse>(`/transcriptions/${transcriptionId}/speaker`, req);
  return response.data;
}

export interface RevertResponse {
  id: string;
  is_edited: boolean;
  message: string;
}

export interface UpdateDisplayNameRequest {
  display_name: string;
}

export async function updateTranscriptionDisplayName(
  transcriptionId: string,
  displayName: string
): Promise<TranscriptionResponse> {
  const response = await apiClient.put<TranscriptionResponse>(
    `/transcriptions/${transcriptionId}/display-name`,
    { display_name: displayName } satisfies UpdateDisplayNameRequest
  );
  return response.data;
}

export async function revertTranscription(transcriptionId: string): Promise<RevertResponse> {
  const response = await apiClient.post<RevertResponse>(`/transcriptions/${transcriptionId}/revert`);
  return response.data;
}

// Cancel transcription
export async function cancelTranscription(transcriptionId: string): Promise<TranscriptionResponse> {
  const response = await apiClient.post<TranscriptionResponse>(`/transcriptions/${transcriptionId}/cancel`);
  return response.data;
}

// Retry failed transcription
export async function retryTranscription(transcriptionId: string): Promise<TranscriptionResponse> {
  const response = await apiClient.post<TranscriptionResponse>(`/transcriptions/${transcriptionId}/retry`);
  return response.data;
}

// Delete transcription
export async function deleteTranscription(transcriptionId: string): Promise<void> {
  await apiClient.delete(`/transcriptions/${transcriptionId}`);
}

// Get transcription statistics
export interface TranscriptionStatsResponse {
  total_transcriptions: number;
  total_minutes: number;
  transcriptions_by_status: Record<string, number>;
}

export async function getTranscriptionStats(): Promise<TranscriptionStatsResponse> {
  const response = await apiClient.get<TranscriptionStatsResponse>('/transcriptions/stats');
  return response.data;
}

// Get transcription by file ID (returns first transcription for the file, or null if none)
export async function getTranscriptionByFileId(fileId: string): Promise<TranscriptionResponse | null> {
  const response = await apiClient.get<ListTranscriptionsResponse>('/transcriptions', {
    params: { file_id: fileId, page_size: 1 }
  });
  return response.data.transcriptions[0] || null;
}

// ============================================================================
// Translation API Types
// ============================================================================

// Translated segment from backend
export interface TranslatedSegment {
  id: number;
  translation: string;
}

// Translation item in list response
export interface TranslationItem {
  language: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  is_stale: boolean;
  progress?: number;
  created_at?: string;
}

// List translations response
export interface ListTranslationsApiResponse {
  translations: TranslationItem[];
}

// Full translation file from backend
export interface TranslationFile {
  transcription_id: string;
  source_language: string;
  target_language: string;
  source_version: string;
  is_stale: boolean;
  stale_segments?: number[];
  segments: TranslatedSegment[];
  created_at: string;
  updated_at: string;
}

// Translation status response
export interface TranslationStatusApiResponse {
  target_language: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  is_stale?: boolean;
  stale_segment_count?: number;
}

export interface TranslationUpdatesApiResponse {
  target_language: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  progress?: number;
  translations: TranslatedSegment[];
  next_cursor: number;
}

// ============================================================================
// Translation API Functions
// ============================================================================

// List available translations for a transcription
export async function listTranslations(transcriptionId: string): Promise<ListTranslationsApiResponse> {
  const response = await apiClient.get<ListTranslationsApiResponse>(`/transcriptions/${transcriptionId}/translations`);
  return response.data;
}

// Get full translation content for a specific language
export async function getTranslation(transcriptionId: string, lang: string): Promise<TranslationFile> {
  const response = await apiClient.get<TranslationFile>(`/transcriptions/${transcriptionId}/translations/${lang}`);
  return response.data;
}

// Get translation status for a specific language
export async function getTranslationStatus(transcriptionId: string, lang: string): Promise<TranslationStatusApiResponse> {
  const response = await apiClient.get<TranslationStatusApiResponse>(`/transcriptions/${transcriptionId}/translations/${lang}/status`);
  return response.data;
}

export async function getTranslationUpdates(
  transcriptionId: string,
  lang: string,
  cursor: number = 0,
  limit: number = 200
): Promise<TranslationUpdatesApiResponse> {
  const response = await apiClient.get<TranslationUpdatesApiResponse>(
    `/transcriptions/${transcriptionId}/translations/${lang}/updates`,
    { params: { cursor, limit } }
  );
  return response.data;
}

// Delete a translation for a specific language
export async function deleteTranslation(transcriptionId: string, lang: string): Promise<void> {
  await apiClient.delete(`/transcriptions/${transcriptionId}/translations/${lang}`);
}
